import { useCallback, useEffect, useState } from 'react';
import {
  warehouseApi,
  type WarehouseResponse,
  type WmTransfer,
} from '@/api/warehouse.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { ProductSelect } from '@/components/selects';
import { usePermissions } from '@/hooks/usePermissions';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
export function WarehouseTransfersPage() {
  const { hasAny } = usePermissions();
  const canTransfer = hasAny('WAREHOUSE_TRANSFER', 'WAREHOUSE_ADMIN');
  const canApprove = hasAny('WAREHOUSE_APPROVE', 'WAREHOUSE_ADMIN');
  const [transfers, setTransfers] = useState<WmTransfer[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseResponse[]>([]);
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [productId, setProductId] = useState('');
  const [quantity, setQuantity] = useState('1');

  const reload = useCallback(() => {
    warehouseApi.listOpenTransfers().then(setTransfers).catch((e) => showApiError(e, 'Failed to load transfers'));
  }, []);

  useEffect(() => {
    reload();
    warehouseApi.listWarehouses().then(setWarehouses).catch(() => {});
  }, [reload]);

  const createTransfer = async () => {
    if (!fromId || !toId || !productId) return;
    try {
      await warehouseApi.createTransfer({
        fromWarehouseId: Number(fromId),
        toWarehouseId: Number(toId),
        lines: [{ productId: Number(productId), quantity: Number(quantity) }],
      });
      notify.success('Transfer created');
      reload();
    } catch (e) {
      showApiError(e, 'Create transfer failed');
    }
  };

  const runAction = async (id: number, action: 'approve' | 'dispatch' | 'receive') => {
    try {
      if (action === 'approve') await warehouseApi.approveTransfer(id);
      else if (action === 'dispatch') await warehouseApi.dispatchTransfer(id);
      else await warehouseApi.receiveTransfer(id);
      notify.success(`Transfer ${action}d`);
      reload();
    } catch (e) {
      showApiError(e, `Transfer ${action} failed`);
    }
  };

  return (
    <div className="pb-safe">
      <PageHeader
        title="Warehouse transfers"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Warehouse', to: '/warehouse' },
          { label: 'Transfers' },
        ]}
        actions={<Button variant="secondary" onClick={reload}>Refresh</Button>}
      />

      <div className="card mb-6 p-4">
        <h2 className="mb-3 font-semibold">Create transfer</h2>
        {canTransfer ? (
          <>
            <div className="grid gap-3 sm:grid-cols-4">
              <Select
                label="From warehouse"
                value={fromId}
                onChange={(v) => setFromId(v)}
                options={[
                  { value: '', label: 'Select…' },
                  ...warehouses.map((w) => ({ value: String(w.id), label: w.code })),
                ]}
              />
              <Select
                label="To warehouse"
                value={toId}
                onChange={(v) => setToId(v)}
                options={[
                  { value: '', label: 'Select…' },
                  ...warehouses.map((w) => ({ value: String(w.id), label: w.code })),
                ]}
              />
              <ProductSelect label="Product *" value={productId} onChange={setProductId} required />
              <Input label="Quantity" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
            </div>
            <Button className="mt-3" onClick={() => void createTransfer()}>
              Create transfer
            </Button>
          </>
        ) : (
          <p className="text-sm text-slate-500">Transfer permission required to create transfers.</p>
        )}
      </div>

      <ul className="space-y-3">
        {transfers.map((t) => (
          <li key={t.id} className="card flex flex-wrap items-center justify-between gap-2 p-4">
            <div>
              <p className="font-medium">{t.transferCode}</p>
              <p className="text-xs text-slate-500">
                {t.statusCode} ·{' '}
                {warehouses.find((w) => w.id === t.fromWarehouseId)?.code ?? t.fromWarehouseId} →{' '}
                {warehouses.find((w) => w.id === t.toWarehouseId)?.code ?? t.toWarehouseId}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {t.statusCode === 'DRAFT' && canApprove && (
                <Button className="text-sm py-1 px-2" onClick={() => void runAction(t.id, 'approve')}>
                  Approve
                </Button>
              )}
              {t.statusCode === 'APPROVED' && canTransfer && (
                <Button className="text-sm py-1 px-2" onClick={() => void runAction(t.id, 'dispatch')}>
                  Dispatch
                </Button>
              )}
              {t.statusCode === 'IN_TRANSIT' && canTransfer && (
                <Button className="text-sm py-1 px-2" onClick={() => void runAction(t.id, 'receive')}>
                  Receive
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
      {transfers.length === 0 && <p className="text-sm text-slate-500">No open transfers.</p>}
    </div>
  );
}
